/**
 * Cross-tab sync utility for Zustand persist stores
 * PREF-001: Enables sync between browser tabs via storage events
 */
import type { StoreApi, Mutate } from 'zustand';

type StoreWithPersist<T> = Mutate<StoreApi<T>, [['zustand/persist', unknown]]>;

/**
 * Adds cross-tab sync to a Zustand persist store
 * Listens to storage events and rehydrates when data changes in another tab
 *
 * Note: The 'storage' event only fires in OTHER tabs, not the one that made the change.
 * This is exactly what we need - no double-update in originating tab.
 *
 * @param store - Zustand store with persist middleware
 * @returns Cleanup function to remove event listener
 */
export function withStorageSync<T>(store: StoreWithPersist<T>): () => void {
  const storageKey = store.persist.getOptions().name;

  const handleStorageEvent = (e: StorageEvent) => {
    // Only react to our storage key changes from OTHER tabs
    // newValue is null when item is removed (clear), skip rehydrate in that case
    if (e.key === storageKey && e.newValue !== null) {
      store.persist.rehydrate();
    }
  };

  window.addEventListener('storage', handleStorageEvent);

  return () => {
    window.removeEventListener('storage', handleStorageEvent);
  };
}
